import axios from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaBell,
  FaCalendarAlt,
  FaChevronCircleLeft,
  FaChevronCircleRight,
  FaClock,
  FaCopy,
  FaMapMarkerAlt,
  FaVideo,
} from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";

import LanguageSwitcher from "../components/LanguageSwitcher";
import { courses } from "../constants/common";
import { useAuth } from "../context/AuthContext";
import { useReminders } from "../context/RemindersContext";
import { formatCustomDate } from "../utils/formatCustomDate";

const Events = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { t, i18n } = useTranslation();
  const { fetchReminders } = useReminders();
  const [loadingEventId, setLoadingEventId] = useState(null);
  const [copySuccess, setCopySuccess] = useState({});

  // query params
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get("type") || "live";

  const tabMap = { live: 1, upcoming: 2 };
  const initialTab = tabMap[type] || 1;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success"); // "success" or "error"

  const tabs = [
    { id: 1, label: "LiveNow", type: "live" },
    { id: 2, label: "Upcoming", type: "upcoming" },
  ];

  // fetch from API whenever type changes
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/events`, {
          params: { type },
          headers: {
            lang: i18n.language,
            Authorization: `Bearer ${user?.token}`,
          },
        });

        setEvents(response?.data?.data?.data || []);
      } catch (err) {
        if (err.response?.status === 401) {
          setUser(null);
          navigate("/auth/login", { replace: true });
        } else {
          setError("Failed to load events. Please try again later.");
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [type, user?.token, i18n.language]);

  const handleCopyPath = (path, eventId) => {
    const textarea = document.createElement("textarea");
    textarea.value = path;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");

      // set only for this session
      setCopySuccess((prev) => ({ ...prev, [eventId]: true }));

      setTimeout(() => {
        setCopySuccess((prev) => ({ ...prev, [eventId]: false }));
      }, 1500);
    } catch (err) {
      console.error("Copy failed", err);
    }
    document.body.removeChild(textarea);
  };

  const handleSetReminder = async (eventId) => {
    setLoadingEventId(eventId);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reminder`,
        { type: 1, model_id: eventId },
        {
          headers: {
            lang: i18n.language,
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );

      const reminderId = response?.data?.data?.id;
      setEvents((prev) => prev.map((ev) => (ev.id === eventId ? { ...ev, is_reminder: reminderId } : ev)));

      fetchReminders();
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        setError("Failed to load events. Please try again later.");
        console.error(err);
      }
    } finally {
      setLoadingEventId(null);
    }
  };

  const handleRemoveReminder = async (reminderId, eventId) => {
    setLoadingEventId(eventId);
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/reminder/${reminderId}`, {
        headers: {
          lang: i18n.language,
          Authorization: `Bearer ${user?.token}`,
        },
      });

      setEvents((prev) =>
        prev.map((ev) => (ev.is_reminder === reminderId ? { ...ev, is_reminder: null } : ev))
      );

      fetchReminders();
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        setError("Failed to load events. Please try again later.");
        console.error(err);
      }
    } finally {
      setLoadingEventId(null);
    }
  };

  const handleEventClick = (event) => {
    navigate(
      `/meeting?link=${encodeURIComponent(
        event.location
      )}&meetingID=${event.meeting_id}&sessionID=${event.id}&type=2`
    );
  };

  useEffect(() => {
    if (type && tabMap[type]) {
      setActiveTab(tabMap[type]);
    }
  }, [type]);

  const handleSubmitCourseSelection = async () => {
    if (!selectedEvent) return;

    if (selectedCourses.length === 0) {
      setPopupMessage(t("PleaseSelectAtLeastOneCourse"));
      setPopupType("error");
      setTimeout(() => setPopupMessage(""), 3000);
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/event-register/${selectedEvent.id}`,
        {
          courses: selectedCourses, // sends array of selected IDs
        },
        {
          headers: {
            lang: i18n.language,
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );

      setPopupMessage(t("RegistrationSuccessful"));
      setPopupType("success");
      setTimeout(() => setPopupMessage(""), 3000);

      // update event to show registered
      setEvents((prev) =>
        prev.map((ev) => (ev.id === selectedEvent.id ? { ...ev, is_registered: true } : ev))
      );
    } catch (err) {
      console.error(err);
      setPopupMessage(t("ErrorOccurred"));
      setPopupType("error");
      setTimeout(() => setPopupMessage(""), 3000);
    }

    setShowRegisterModal(false);
    setSelectedEvent(null);
    setSelectedCourses([]);
  };

  return (
    <>
      <div className="mx-auto max-w-6xl space-y-6 p-4 pt-0">
        {/* Back button */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-purple-500 hover:text-purple-500"
          >
            {i18n.language == "ar" ? (
              <FaChevronCircleRight size={18} className="text-purple-500" />
            ) : (
              <FaChevronCircleLeft size={18} className="text-purple-500" />
            )}
            {t("back")}
          </button>
          <div className="hidden justify-end lg:flex">{/* <LanguageSwitcher defaultTheme />{" "} */}</div>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-3">
          {tabs.map(({ id, label, type }) => (
            <button
              key={id}
              onClick={() => {
                setActiveTab(id);
                queryParams.set("type", type);
                navigate(`?${queryParams.toString()}`, { replace: true });
              }}
              className={`rounded-full px-6 py-2 text-sm font-medium transition-all ${
                activeTab === id
                  ? "bg-purple-600 text-white shadow-md"
                  : "bg-gray-100 text-purple-500 hover:bg-purple-50"
              }`}
            >
              {t(label)}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-6">
            <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600"></div>
          </div>
        ) : error ? (
          <p className="text-center text-red-500">{error}</p>
        ) : events.length === 0 ? (
          <p className="text-center text-gray-500">{t("Noeventsfound")}</p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
            {events.map((event) => {
              // calculate join button availability
              const start = new Date(`${event.start_date}T${event.start_time}`);
              const end = new Date(`${event.start_date}T${event.end_time}`);
              const now = new Date();
              const thresholdStart = new Date(start.getTime() - 15 * 60 * 1000);
              const showJoinButton = now >= thresholdStart && now <= end;

              return (
                <div
                  key={event.id}
                  className="flex flex-col overflow-hidden rounded-xl bg-white shadow-md ring-1 ring-gray-200"
                >
                  {/* Info section */}
                  <div className="flex flex-col gap-3 bg-gradient-to-br from-purple-500/90 to-purple-600/90 p-4 text-white">
                    {/* Badge */}
                    <div className="flex items-center gap-2">
                      {activeTab === 1 ? (
                        <span className="rounded-full bg-red-500 px-3 py-1 text-xs font-semibold uppercase">
                          {t("LiveNow")}
                        </span>
                      ) : (
                        <span className="inline-block rounded-full bg-yellow-400 px-3 py-1 text-xs font-bold uppercase text-white shadow">
                          {t("Upcoming")}
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                        <div className="flex w-full flex-col overflow-hidden sm:flex-row sm:items-center sm:gap-3">
                          <div className="flex items-center gap-2">
                            <p className="flex-1 text-sm font-bold text-white sm:text-base">{event?.name}</p>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs sm:text-sm">{event?.description}</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 text-sm">
                        {/* Date with clock icon */}
                        <div className="flex items-center gap-1 py-1 font-medium">
                          <FaClock className="" />
                          <span>{formatCustomDate(event?.start_date)}</span>
                        </div>

                        {/* Time range */}
                        <div className="px-2 py-1">
                          {event?.start_time} - {event?.end_time}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        {event?.coaches?.map((item, index) => (
                          <span
                            key={index}
                            className="whitespace-nowrap text-xs italic text-purple-100 sm:text-sm"
                          >
                            {index > 0 && <span className="mx-1 text-purple-400">/</span>}
                            {item}
                          </span>
                        ))}
                      </div>
                      {activeTab == 2 ? (
                        event?.is_online ? (
                          <p className="text-xs">*{t("upcomingEventsAlert")}</p>
                        ) : null
                      ) : null}
                    </div>
                  </div>

                  <div className="p-3 sm:p-4">
                    {/* Online / Location */}
                    {event?.is_online ? (
                      <div className="flex items-center gap-2">
                        {showJoinButton && (
                          <button
                            onClick={() => handleEventClick(event)}
                            className="flex items-center gap-2 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 px-4 py-2 text-xs font-medium text-white shadow-lg transition sm:px-5 sm:text-sm"
                          >
                            <FaVideo size={14} /> {t("JoinEvent")}
                          </button>
                        )}
                        <button
                          onClick={() =>
                            handleCopyPath(
                              `${process.env.REACT_APP_BASE_URL}meeting?link=${encodeURIComponent(
                                event.location
                              )}&meetingID=${event.meeting_id}&sessionID=${event.id}&type=2`,
                              event.id
                            )
                          }
                          className="flex items-center gap-2 rounded-full border border-purple-500 px-4 py-2 text-xs font-medium text-purple-500 shadow-lg transition hover:bg-purple-50 hover:text-purple-700 sm:px-5 sm:text-sm"
                        >
                          <FaCopy /> {copySuccess[event.id] ? t("Copied") : t("CopyLink")}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-2">
                        {/* Reminder Button on Image */}

                        <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-purple-500 sm:text-sm">
                          <FaMapMarkerAlt size={14} />
                          <a
                            href={event?.location}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-500 underline hover:text-purple-900"
                          >
                            {event?.governorate?.name}
                          </a>
                        </p>
                        {activeTab === 2 && (
                          <div className="flex items-center gap-2">
                            {event.is_online !== 1 &&
                              (event.is_registered ? (
                                <span className="rounded-full bg-green-500 px-3 py-2 text-xs font-semibold uppercase text-white">
                                  {t("youAreRegistered")}
                                </span>
                              ) : (
                                <button
                                  onClick={() => {
                                    setSelectedEvent(event);
                                    setShowRegisterModal(true);
                                  }}
                                  className="flex items-center gap-2 rounded-full bg-gradient-to-br from-purple-600 to-purple-700 px-4 py-[6.2px] text-xs font-medium text-white shadow-lg hover:opacity-90 sm:px-5 sm:text-sm"
                                >
                                  {t("ConfirmAttendance")}
                                </button>
                              ))}

                            <button
                              onClick={
                                event.is_reminder
                                  ? () => handleRemoveReminder(event.is_reminder, event.id)
                                  : () => handleSetReminder(event.id)
                              }
                              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium shadow-lg transition-all duration-300 sm:px-4 sm:text-sm ${
                                event.is_reminder
                                  ? "border border-purple-600 bg-white text-purple-600 hover:bg-purple-50"
                                  : "bg-gradient-to-br from-purple-500 to-purple-600 text-white hover:from-purple-500 hover:to-purple-600"
                              } ${loadingEventId === event.id ? "cursor-not-allowed opacity-50" : ""}`}
                            >
                              {loadingEventId === event.id ? (
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-purple-600"></div>
                              ) : (
                                <FaBell
                                  size={14}
                                  className={`transition-colors duration-300 ${
                                    event.is_reminder ? "text-purple-600" : "text-white"
                                  }`}
                                />
                              )}
                              {event.is_reminder ? t("RemoveReminder") : t("SetReminder")}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {showRegisterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-bold text-purple-700">{t("SelectCourses")}</h2>

            {/* Courses List */}
            <div className="mb-4 max-h-64 space-y-3 overflow-y-auto">
              {courses.map((course) => (
                <label
                  key={course.id}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 hover:bg-purple-50"
                >
                  <input
                    type="checkbox"
                    checked={selectedCourses.includes(course.id)}
                    onChange={() => {
                      setSelectedCourses((prev) =>
                        prev.includes(course.id) ? prev.filter((c) => c !== course.id) : [...prev, course.id]
                      );
                    }}
                    className="h-4 w-4 text-purple-600 accent-purple-600"
                  />
                  <span className="text-sm">{i18n.language === "ar" ? course.name_ar : course.name_en}</span>
                </label>
              ))}
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRegisterModal(false);
                  setSelectedCourses([]);
                }}
                className="rounded-full border border-gray-300 px-4 py-2 text-sm hover:bg-gray-100"
              >
                {t("Cancel")}
              </button>

              <button
                onClick={() => handleSubmitCourseSelection()}
                className="rounded-full bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
              >
                {t("Confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {popupMessage && (
        <div
          className={`fixed bottom-5 left-1/2 z-50 w-auto max-w-xs -translate-x-1/2 rounded-lg px-4 py-3 text-white shadow-lg transition-all duration-300 ${
            popupType === "success" ? "bg-green-600" : "bg-red-600"
          }`}
        >
          {popupMessage}
        </div>
      )}
    </>
  );
};

export default Events;
